import { useCallback, useEffect, useState } from "react";
import { type RelationGroupId, toRelationGroupId } from "utilities";

const STORAGE_PREFIX = "tt_relation_group:";

/**
 * localStorage throws rather than no-ops in some privacy modes and embedded webviews, so both
 * accessors fail closed. An unreadable store just means the canvas opens on "All", which is the
 * default anyway — nothing breaks.
 */
const readStoredGroup = (treeId: string | undefined): RelationGroupId => {
	if (!treeId) {
		return "all";
	}

	try {
		return toRelationGroupId(window.localStorage.getItem(STORAGE_PREFIX + treeId));
	} catch {
		return "all";
	}
};

const storeGroup = (treeId: string | undefined, group: RelationGroupId): void => {
	if (!treeId) {
		return;
	}

	try {
		window.localStorage.setItem(STORAGE_PREFIX + treeId, group);
	} catch {
		// Storage unavailable; the choice simply does not survive the visit.
	}
};

/**
 * Which relation context the canvas is drawing, remembered per tree for this viewer.
 *
 * Per tree rather than globally: someone reading a family lineage and someone reading a war
 * chronicle want different lenses, and carrying one tree's choice into the next would hide edges
 * for no reason the reader can see. Stored per browser, so it needs no API and no account.
 */
export const useRelationGroup = (
	treeId: string | undefined
): { relationGroup: RelationGroupId; setRelationGroup: (group: RelationGroupId) => void } => {
	const [relationGroup, setGroup] = useState<RelationGroupId>(() => readStoredGroup(treeId));

	// Opening a different tree adopts that tree's remembered choice rather than keeping this one.
	useEffect(() => {
		setGroup(readStoredGroup(treeId));
	}, [treeId]);

	const setRelationGroup = useCallback(
		(group: RelationGroupId): void => {
			setGroup(group);
			storeGroup(treeId, group);
		},
		[treeId]
	);

	return { relationGroup, setRelationGroup };
};
